import * as Sentry from '@sentry/react';
import { Navigate } from 'react-router';
import type {
  BootstrapContext,
  BootstrapGateProps,
  ProtectedRouteProps,
} from './interfaces';
import { PageSkeleton } from '@/components/shared/page-skeleton';
import { useUser } from '@/data/user';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import type { RouteAccess } from '@/routes/interfaces';
import { hasAccess } from '@/utils/user-permissions';

const canAccessRoute = (ctx: BootstrapContext, route: RouteAccess) => {
  return hasAccess(ctx, {
    permissions: route.permissions,
    features: route.features,
    check: route.check,
  });
};

const BootstrapGate = ({ children }: BootstrapGateProps) => {
  const { user, isLoading: isLoadingUser } = useUser(true);
  const { bootstrap, isLoading: isLoadingBootstrap } = useBootstrapData(true);

  const isLoading = isLoadingUser || isLoadingBootstrap;
  const isUnauthorized = !user || !bootstrap;

  if (isLoading) return <PageSkeleton />;

  if (isUnauthorized) {
    localStorage.clear();
    Sentry.setUser(null);
    return <Navigate to="/login" replace />;
  }

  Sentry.setUser({ id: user?.id, email: user?.email, username: user?.nome });
  Sentry.setContext('userInfo', {
    companyId: user?.colaborador?.empresa_id,
    companyName: user?.colaborador?.empresa?.empresa,
    features: bootstrap?.plan?.features.map((feature) => feature.name),
    permissions: user?.permissoes?.map((permission) => permission.nome),
  });

  return children({ user, bootstrap });
};

export const ProtectedRoute = ({ route, children }: ProtectedRouteProps) => {
  return (
    <BootstrapGate>
      {({ user, bootstrap }) => {
        if (!route.skipTermsCheck && !user?.terms_accepted) {
          return <Navigate to="/termos" replace />;
        }

        if (!canAccessRoute({ user, bootstrap }, route)) {
          return <Navigate to="/" replace />;
        }

        return <>{children}</>;
      }}
    </BootstrapGate>
  );
};
